/*
UnifiedInternal
==============
*/
export const AssistantFeedbackType = [
	'thumbs-up',
	'thumbs-down'
] as const;

export type TAssistantFeedbackType = typeof AssistantFeedbackType[number];

export const AssistantSource = [
	'slack',
	'discord',
	'app',
	'oneeye'
] as const;

export type TAssistantSource = typeof AssistantSource[number];

export const BetaIntegrationCategory = [
	'genai',
	'messaging',
	'calendar'
] as const;

export type TBetaIntegrationCategory = typeof BetaIntegrationCategory[number];

export const BetaObjectType = [
	'genai_model',
	'genai_prompt'
] as const;

export type TBetaObjectType = typeof BetaObjectType[number];

export const IntegrationInstructionLanguages = [
	'en',
	'fr',
	'es',
	'it',
	'pt',
	'hi',
	'zh',
	'de'
] as const;

export type TIntegrationInstructionLanguages = typeof IntegrationInstructionLanguages[number];

export const InstructionBlockType = [
	'h1',
	'h2',
	'h3',
	'p',
	'ol',
	'ul',
	'>',
	'!',
	'image'
] as const;

export type TInstructionBlockType = typeof InstructionBlockType[number];

export const EnterpriseAuthTopology = [
	'direct_xaa',
	'isv_brokered'
] as const;

export type TEnterpriseAuthTopology = typeof EnterpriseAuthTopology[number];

export const EnterpriseAuthKeySource = [
	'jwks',
	'workspace_secret',
	'static_key'
] as const;

export type TEnterpriseAuthKeySource = typeof EnterpriseAuthKeySource[number];

export interface IArticle {
	id?: string;
	created_at?: (string | Date | number);
	updated_at?: (string | Date | number);
	published_at?: (string | Date | number);
	page_xref: string;
	icon_url?: string;
	title: string;
	content: string; // markdown
	tags?: string[];
	description?: string;
	filename?: string;
	integrations?: string[]; // integration types this article is relevant to (from the Notion "Integrations" field)
}

export interface IAssistant {
	id?: string;
	created_at?: (string | Date | number);
	updated_at?: (string | Date | number);
	workspace_id?: string;
	user_id?: string;
	user_email?: string;
	user_name?: string;
	source?: TAssistantSource;
	source_url?: string;
	user_question?: string;
	assistant_answer?: string;
	assistant_citations?: string[];
	user_feedback?: TAssistantFeedbackType;
}

export interface ICustomer {
	id?: string;
	created_at?: (string | Date | number);
	updated_at?: (string | Date | number);
	name: string;
	workspace_id?: string;
	dc?: string;
	channel_url?: string;
	channel_id?: string;
	channel_type?: string; // slack or discord
	notifications_opted_in?: boolean; // From Notion CRM "Notification preference"; blank defaults to true.
	customer_xref: string; // Notion ID
	added_at?: (string | Date | number);
	channel_created_at?: (string | Date | number);
	discovery_call_at?: (string | Date | number);
	onboarding_at?: (string | Date | number);
	registered_at?: (string | Date | number);
	upgraded_at?: (string | Date | number);
	last_contacted_at?: (string | Date | number);
	status?: string;
	is_qualified?: boolean; // Lead Quality is Expanding their stack, Migrating from competitor, Target ICP
	g2review_received_at?: (string | Date | number);
	g2review_requested_at?: (string | Date | number);
}

export interface IDataShapeIssue {
	id?: string;
	created_at?: (string | Date | number);
	updated_at?: (string | Date | number);
	resolved_at?: (string | Date | number);
	integration_type?: string;
	connection_ids: string[];
	issue: string;
	instances: number;
	object_type?: string;
}

export interface IGlossaryEntry {
	id?: string;
	created_at?: (string | Date | number);
	updated_at?: (string | Date | number);
	page_xref: string;
	title: string;
	description: string;
	content: string; // markdown
	filename: string;
}

export interface IIntegrationInstruction {
	id?: string;
	created_at?: (string | Date | number);
	updated_at?: (string | Date | number);
	page_xref: string;
	integration_type: string;
	title?: string;
	instructions: IIntegrationInstructionBlock[];
}

export interface IIntegrationInstructionBlock {
	block_type: string;
	image_url?: string;
	text?: { [path in TIntegrationInstructionLanguages]?: string };
}

export interface IIssuedAccessToken {
	id?: string;
	created_at?: (string | Date | number);
	updated_at?: (string | Date | number);
	workspace_id: string;
	trusted_issuer_id?: string;
	identity_id?: string;
	user_id?: string; // resolved workspace user (populates the auth context)
	token_hash: string; // sha-256 of the opaque bearer token; never store the token itself
	scopes?: string[];
	environment?: string; // environment gate parity with API keys
	resource?: string; // RFC 8707 resource the token is bound to; absent means the REST API
	expires_at: (string | Date | number);
	revoked_at?: (string | Date | number);
	last_used_at?: (string | Date | number);
}

export interface IKey {
	id?: string;
	created_at?: (string | Date | number);
	updated_at?: (string | Date | number);
	workspace_id: string;
	user_id: string;
	iat: number;
	nonce: string;
	environments?: string[];
}

export interface IPlan {
	name: string;
	description?: string; // description
	stripe_product_id?: string;
	test_stripe_product_id?: string;
	stripe_price_id?: string;
	test_stripe_price_id?: string;
	stripe_annual_price_id?: string;
	test_stripe_annual_price_id?: string;
	stripe_overage_price_id?: string;
	test_stripe_overage_price_id?: string;
	min_price?: number;
	min_annual_price?: number;
	price_per_mille_requests?: number;
	max_requests?: number;
	max_connections?: number; // Maximum active connections (1+ API call per month)
	custom_oauth?: boolean;
	is_active?: boolean;
	dedicated_channel?: boolean;
	rate_limit?: number; // api calls per second
	has_virtual_webhooks?: boolean;
	concurrent_download_limit?: number;
}

export interface IRb2bVisit {
	id?: string;
	created_at?: (string | Date | number);
	updated_at?: (string | Date | number);
	seen_at: (string | Date | number); // RB2B "Seen At"
	email?: string; // RB2B "Business Email", lower-cased
	domain?: string; // from RB2B "Website", e.g. acme.com
	linkedin_url?: string;
	first_name?: string;
	last_name?: string;
	title?: string;
	company_name?: string;
	website?: string;
	industry?: string;
	employee_count?: string; // a number or a range, e.g. 11-50
	estimated_revenue?: string; // a range, e.g. $1M - $5M
	city?: string;
	state?: string;
	zipcode?: string;
	referrer?: string;
	captured_url?: string; // the page they visited
	tags?: string;
	is_repeat_visitor?: boolean;
	raw?: unknown;
}

export interface ISandbox {
	workspace_id: string;
	integration_type: string;
	object_type: string;
	data: unknown;
}

export interface ISuperAdminAudit {
	id?: string;
	created_at?: (string | Date | number);
	audit_kind: string;
	path?: string;
	method?: string;
	status?: string;
	error?: string;
	user_id?: string;
	ip_address?: string;
	user_agent?: string;
	workspace_id?: string;
	connection_id?: string;
	integration_type?: string;
	environment?: string;
	source?: string;
	read_only?: boolean;
	object_types?: string[];
	summary_errors?: number;
	summary_warnings?: number;
	summary_successes?: number;
}

export interface IWebhookCache {
	id?: string;
	object_type: string;
	sha256: string; // sha-256 hash of the original data (used for diff)
	connection_id: string; // indexed
	workspace_id: string; // indexed
	synced_at: (string | Date | number); // date last synced
	deleted_at?: (string | Date | number);
	updated_at: (string | Date | number);
	created_at?: (string | Date | number);
	object_id: string; // indexed
	data?: unknown; // will be used for those customers that want us to store their data
	ref_ids?: IWebhookCacheFilter[];
}

export interface IWebhookCacheFilter {
	key: string;
	value: string;
	id: string;
}

export interface IWebhookSyncToken {
	id?: string;
	created_at?: (string | Date | number);
	updated_at?: (string | Date | number);
	webhook_id: string;
	key: string;
	value: string;
}

export interface IWorkspaceIdentity {
	id?: string;
	created_at?: (string | Date | number);
	updated_at?: (string | Date | number);
	workspace_id: string;
	trusted_issuer_id: string;
	subject: string; // the assertion `sub`
	email?: string; // from the `email` claim if present
	user_id?: string; // workspace user; unset until mapped
	last_seen_at?: (string | Date | number);
}

export interface IWorkspaceTrustedIssuer {
	id?: string;
	created_at?: (string | Date | number);
	updated_at?: (string | Date | number);
	workspace_id: string;
	mode: TEnterpriseAuthTopology;
	issuer: string; // exact `iss` expected in the redeemed assertion
	jwks_uri?: string; // when key_source = 'jwks'
	key_source: TEnterpriseAuthKeySource;
	static_public_key?: string; // PEM, when key_source = 'static_key'
	client_ids_to_allow?: string[]; // allowed `client_id` values; empty/undefined = allow any
	default_scopes?: string[]; // scopes granted when the assertion carries none
	access_token_ttl_seconds?: number; // per-issuer TTL override (else setting/default)
	environment?: string; // when set, tokens are bound to this environment (environments gate applies)
	jit_enabled?: boolean; // create/update identities on first use (email-match)
	enabled: boolean;
}

export interface IEnterpriseAuthSetting {
	id?: string;
	created_at?: (string | Date | number);
	updated_at?: (string | Date | number);
	workspace_id: string; // unique
	enabled: boolean; // master feature flag; default false
	default_access_token_ttl_seconds?: number; // fallback TTL when an issuer omits one
}

export const PLANS: IPlan[] = [
    { name: 'Test', description: 'Get 30 days of unlimited access to test our unified APIs in non-production applications. No credit card needed.', max_connections: 0, min_price: 0, max_requests: 0, is_active: false, custom_oauth: true, concurrent_download_limit: 10 },
    { name: 'Start', description: 'For launching unified APIs in production applications.', min_price: 250, custom_oauth: true, is_active: false, price_per_mille_requests: 1, max_requests: 250000, stripe_product_id: 'prod_NpKIL9HEvIfdx2', test_stripe_product_id: 'prod_Np1xniikGxq80G', stripe_annual_price_id: 'price_1NiQOiCym65m1EMKfAvlrjq4', min_annual_price: 2500, stripe_price_id: 'price_1NkJPLCym65m1EMKSLqJhKQj', test_stripe_price_id: 'price_1NkISrCym65m1EMKRV9bJsqI', test_stripe_annual_price_id: 'price_1NkFggCym65m1EMKQ88k8pZo', stripe_overage_price_id: 'price_1RAvUGCym65m1EMKjNv4Wlji', test_stripe_overage_price_id: 'price_1NkH5QCym65m1EMKmx9QKFl4', concurrent_download_limit: 25 },
    { name: 'Grow', description: 'For production applications with growing customer usage.', min_price: 500, stripe_product_id: 'prod_NpKIvppVcyFEy6', max_requests: 1250000, price_per_mille_requests: 0.4, custom_oauth: true, is_active: false, test_stripe_product_id: 'prod_OXNbPZfryQINTy', stripe_annual_price_id: 'price_1NkEWJCym65m1EMKalcplitD', stripe_price_id: 'price_1NkJXtCym65m1EMKFb6zMvGj', test_stripe_price_id: 'price_1NkIq7Cym65m1EMKFAFYEAeT', test_stripe_annual_price_id: 'price_1NkIq8Cym65m1EMKv84PiotO', stripe_overage_price_id: 'price_1RAvYqCym65m1EMK209e82Wk', test_stripe_overage_price_id: 'price_1NkIq7Cym65m1EMKh3nXHWaS', min_annual_price: 5000, concurrent_download_limit: 50 },
    { name: 'Scale', description: 'For large-scale applications with very high customer usage.', min_price: 1000, max_requests: 4000000, custom_oauth: true, is_active: false, price_per_mille_requests: 0.25, stripe_product_id: 'prod_NpKIcO1Qitzh3t', test_stripe_price_id: 'price_1NkISrCym65m1EMKRV9bJsqI', stripe_annual_price_id: 'price_1NiQMgCym65m1EMKYgaEaD0r', min_annual_price: 10000, stripe_price_id: 'price_1NkJdPCym65m1EMKIXLloDTw', test_stripe_product_id: 'prod_NpKHMlZb3JXloL', test_stripe_annual_price_id: 'price_1NkFggCym65m1EMKQ88k8pZo', stripe_overage_price_id: 'price_1RAvSkCym65m1EMKVRwx1DmD', test_stripe_overage_price_id: 'price_1NkH5QCym65m1EMKmx9QKFl4', concurrent_download_limit: 100 },
    { stripe_overage_price_id: 'price_1RAvcVCym65m1EMKZkfVP5t5', name: 'Start', description: 'For launching unified APIs in production applications.', min_price: 250, custom_oauth: true, is_active: false, price_per_mille_requests: 2, max_requests: 125000, stripe_product_id: 'prod_NpKIL9HEvIfdx2', test_stripe_product_id: 'prod_Np1xniikGxq80G', stripe_price_id: 'price_1OIXv2Cym65m1EMKpjPwj8ng', stripe_annual_price_id: 'price_1OIXwKCym65m1EMKs4oOuC15', min_annual_price: 2500, concurrent_download_limit: 25 },
    { name: 'Grow', description: 'For production applications with growing customer usage. ', stripe_product_id: 'prod_NpKIvppVcyFEy6', max_requests: 750000, price_per_mille_requests: 1, custom_oauth: true, is_active: false, min_annual_price: 7500, min_price: 750, stripe_price_id: 'price_1OIY1qCym65m1EMKojDSLMzD', stripe_annual_price_id: 'price_1OIY3CCym65m1EMKoErsC4wM', stripe_overage_price_id: 'price_1OIY4hCym65m1EMKCLPFKTFm', concurrent_download_limit: 50 },
    { name: 'Scale', description: 'For large-scale applications with very high customer usage.', min_price: 2000, min_annual_price: 20000, price_per_mille_requests: 0.5, max_requests: 4000000, custom_oauth: true, is_active: false, stripe_product_id: 'prod_NpKIcO1Qitzh3t', stripe_price_id: 'price_1OIY9PCym65m1EMKLwOTJ3WJ', stripe_annual_price_id: 'price_1OIYAUCym65m1EMKeyisdlgc', stripe_overage_price_id: 'price_1OIYDeCym65m1EMKqxSgCtiD', concurrent_download_limit: 100 },
    { name: 'Start', min_price: 100, custom_oauth: true, is_active: false, price_per_mille_requests: 1, max_requests: 200000, stripe_product_id: 'prod_NpKIL9HEvIfdx2', stripe_annual_price_id: 'price_1NiRJcCym65m1EMKBQRlxUZn', min_annual_price: 1000, stripe_price_id: 'price_1NkJTxCym65m1EMKoqLQqzNy', stripe_overage_price_id: 'price_1RAvbHCym65m1EMKYr6AmoC0', concurrent_download_limit: 25 },
    { stripe_overage_price_id: 'price_1RAvVYCym65m1EMKK5XNtpVH', name: 'Start', description: 'For launching unified APIs in production applications.', min_price: 250, custom_oauth: true, is_active: false, price_per_mille_requests: 5, max_requests: 50000, stripe_product_id: 'prod_NpKIL9HEvIfdx2', test_stripe_product_id: 'prod_Np1xniikGxq80G', stripe_price_id: 'price_1OrShdCym65m1EMKT1hOqAq4', stripe_annual_price_id: 'price_1OIXwKCym65m1EMKs4oOuC15', min_annual_price: 2500, dedicated_channel: true, concurrent_download_limit: 25 },
    { name: 'Grow', description: 'For production applications with growing customer usage. ', stripe_product_id: 'prod_NpKIvppVcyFEy6', max_requests: 500000, price_per_mille_requests: 2, custom_oauth: true, is_active: false, min_annual_price: 10000, min_price: 1000, stripe_price_id: 'price_1OpVEzCym65m1EMKhQAVD42S', stripe_annual_price_id: 'price_1OpVG1Cym65m1EMKDMKP4s7D', stripe_overage_price_id: 'price_1OpVIuCym65m1EMKWJBenokw', dedicated_channel: true, concurrent_download_limit: 50 },
    { name: 'Scale', description: 'For large-scale applications with very high customer usage.', min_price: 5000, min_annual_price: 50000, price_per_mille_requests: 0.5, max_requests: 10000000, custom_oauth: true, is_active: false, stripe_product_id: 'prod_NpKIcO1Qitzh3t', stripe_price_id: 'price_1OpVM1Cym65m1EMK3gfW8LlC', stripe_annual_price_id: 'price_1OpVOnCym65m1EMKUanRKcKV', stripe_overage_price_id: 'price_1OpVNoCym65m1EMKrj4N0Wqs', dedicated_channel: true, concurrent_download_limit: 100 },
    { name: 'Test', description: 'Try free for 30 days in non-production applications. No credit card required.', stripe_product_id: ' ', test_stripe_product_id: ' ', stripe_price_id: ' ', test_stripe_price_id: ' ', stripe_annual_price_id: ' ', test_stripe_annual_price_id: ' ', stripe_overage_price_id: ' ', test_stripe_overage_price_id: ' ', min_price: 0, min_annual_price: 0, price_per_mille_requests: 0, max_requests: 10000, max_connections: 0, custom_oauth: true, is_active: true, dedicated_channel: true, rate_limit: 500, has_virtual_webhooks: true, concurrent_download_limit: 10 },
    { name: 'Start', description: 'For launching unified APIs in small-scale production applications.', stripe_product_id: 'prod_NpKIL9HEvIfdx2', test_stripe_product_id: ' ', stripe_price_id: 'price_1RAvIECym65m1EMKNLEpHPMv', test_stripe_price_id: ' ', stripe_annual_price_id: 'price_1RAvNKCym65m1EMKtEHmLc8q', test_stripe_annual_price_id: ' ', stripe_overage_price_id: 'price_1RAvWlCym65m1EMKmmWFepxl', test_stripe_overage_price_id: ' ', min_price: 250, min_annual_price: 2500, price_per_mille_requests: 5, max_requests: 50000, max_connections: 100, custom_oauth: true, is_active: false, dedicated_channel: true, rate_limit: 1000, has_virtual_webhooks: true, concurrent_download_limit: 25 },
    { name: 'Grow', description: 'For production applications with growing customer usage.', stripe_product_id: 'prod_NpKIvppVcyFEy6', test_stripe_product_id: ' ', stripe_price_id: 'price_1RAvMaCym65m1EMKxC8RaXw2', test_stripe_price_id: ' ', stripe_annual_price_id: 'price_1RBjewCym65m1EMK8kxfTzlN', test_stripe_annual_price_id: ' ', stripe_overage_price_id: 'price_1RAvZlCym65m1EMKQ0fBxws7', test_stripe_overage_price_id: ' ', min_price: 750, min_annual_price: 7500, price_per_mille_requests: 1, max_requests: 750000, max_connections: 0, custom_oauth: true, is_active: true, dedicated_channel: true, rate_limit: 5000, has_virtual_webhooks: true, concurrent_download_limit: 50 },
    { name: 'Scale', description: 'For large-scale applications with high customer usage.', stripe_product_id: 'prod_NpKIcO1Qitzh3t', test_stripe_product_id: ' ', stripe_price_id: 'price_1RBjfcCym65m1EMKBL3PxecY', test_stripe_price_id: ' ', stripe_annual_price_id: 'price_1RBjgUCym65m1EMKxTDW3E5j', test_stripe_annual_price_id: ' ', stripe_overage_price_id: 'price_1RBjcMCym65m1EMKMIw7cbRi', test_stripe_overage_price_id: ' ', min_price: 3000, min_annual_price: 30000, price_per_mille_requests: 0.5, max_requests: 6000000, max_connections: 0, custom_oauth: true, is_active: true, dedicated_channel: true, rate_limit: 10000, has_virtual_webhooks: true, concurrent_download_limit: 100 },
    { name: 'Start', description: 'For launching unified APIs in small-scale production applications.', stripe_product_id: 'prod_NpKIL9HEvIfdx2', test_stripe_product_id: '', stripe_price_id: 'price_1RAvLlCym65m1EMKQSkuD0j3', test_stripe_price_id: '', stripe_annual_price_id: 'price_1RBjhQCym65m1EMKWKlLTXa6', test_stripe_annual_price_id: '', stripe_overage_price_id: 'price_1RAvXoCym65m1EMKdlS0iimk', test_stripe_overage_price_id: '', min_price: 350, min_annual_price: 3500, price_per_mille_requests: 7, max_requests: 50000, max_connections: 25, custom_oauth: true, is_active: false, dedicated_channel: true, rate_limit: 500, has_virtual_webhooks: false, concurrent_download_limit: 25 },
    { name: 'Pro', description: 'For secure production applications with high customer usage.', stripe_product_id: 'prod_TpQTjgigqfPwwF', test_stripe_product_id: '', stripe_price_id: 'price_1SrlcsCym65m1EMKzGJRdKnU', test_stripe_price_id: '', stripe_annual_price_id: 'price_1SrlcsCym65m1EMKknCpTCbu', test_stripe_annual_price_id: '', stripe_overage_price_id: 'price_1SrljlCym65m1EMKiXh2kmt8', test_stripe_overage_price_id: '', min_price: 1250, min_annual_price: 12500, price_per_mille_requests: 1, max_requests: 1250000, max_connections: 0, custom_oauth: true, is_active: false, dedicated_channel: true, rate_limit: 7500, has_virtual_webhooks: true, concurrent_download_limit: 75 },
    { name: 'Scale', description: 'Custom Scale - $5k/month', stripe_product_id: 'prod_UAJ9X2w0Ir2Orj', test_stripe_product_id: '', stripe_price_id: 'price_1TByX4Cym65m1EMKhNw2t9ny', test_stripe_price_id: '', stripe_annual_price_id: '', test_stripe_annual_price_id: '', stripe_overage_price_id: 'price_1TByaHCym65m1EMKiSPMEFG1', test_stripe_overage_price_id: '', min_price: 5000, min_annual_price: 0, price_per_mille_requests: 0.00025, max_requests: 20000000, max_connections: 0, custom_oauth: true, is_active: false, dedicated_channel: true, rate_limit: 0, has_virtual_webhooks: true, concurrent_download_limit: 0 },
    { name: 'Pro', description: 'For secure production applications with high customer usage.', stripe_product_id: 'prod_TpQTjgigqfPwwF', test_stripe_product_id: '', stripe_price_id: 'price_1TjgjGCym65m1EMKLnrKc4sT', test_stripe_price_id: '', stripe_annual_price_id: 'price_1Tjgq4Cym65m1EMK1EKOgxlG', test_stripe_annual_price_id: '', stripe_overage_price_id: 'price_1TjgoWCym65m1EMKTgDVBxmq', test_stripe_overage_price_id: '', min_price: 1500, min_annual_price: 15000, price_per_mille_requests: 0.75, max_requests: 2000000, max_connections: 0, custom_oauth: true, is_active: true, dedicated_channel: true, rate_limit: 7500, has_virtual_webhooks: true, concurrent_download_limit: 75 }
];

